// The metrics dashboard's window, shared by the page's search params and the router's input.

export const METRICS_PRESETS = ['15m', '1h', '6h', '24h', '7d', '30d'] as const
export type MetricsPreset = (typeof METRICS_PRESETS)[number]
export const DEFAULT_METRICS_PRESET: MetricsPreset = '24h'

export const METRICS_PRESET_MS: Record<MetricsPreset, number> = {
  '15m': 15 * 60_000,
  '1h': 3_600_000,
  '6h': 6 * 3_600_000,
  '24h': 24 * 3_600_000,
  '7d': 7 * 24 * 3_600_000,
  '30d': 30 * 24 * 3_600_000,
}

/** Every scan also reads the previous period, so a 90-day custom window touches 180 days. */
export const METRICS_MAX_SPAN_MS = 90 * 24 * 3_600_000
export const METRICS_MIN_SPAN_MS = 5 * 60_000

/** A preset ending now, or a custom `[from, to)` in epoch ms. */
export type MetricsWindowInput = { range: MetricsPreset } | { from: number; to: number }

export const METRICS_RANGE_OPTIONS = [...METRICS_PRESETS, 'custom'] as const
export type MetricsRangeOption = (typeof METRICS_RANGE_OPTIONS)[number]

export type MetricsSearch = { range: MetricsRangeOption; from?: number | undefined; to?: number | undefined }

export const isValidMetricsSpan = (from: number, to: number): boolean =>
  to - from >= METRICS_MIN_SPAN_MS && to - from <= METRICS_MAX_SPAN_MS

/** The window the search names; a custom range missing a bound or outside [5 min, 90 days] falls back to the default. */
export function windowInputOf(search: MetricsSearch): MetricsWindowInput {
  if (search.range !== 'custom') return { range: search.range }
  if (search.from === undefined || search.to === undefined || !isValidMetricsSpan(search.from, search.to))
    return { range: DEFAULT_METRICS_PRESET }
  return { from: search.from, to: search.to }
}
